import { memo } from 'react';
import PropTypes from 'prop-types';
import DrawerPanel from './DrawerPanel.jsx';

/** @type {Record<string, 'default'|'muted'|'purple'|'blue'|'green'|'amber'|'red'>} */
const VARIANT_TO_PANEL = {
  default: 'default',
  muted: 'muted',
  purple: 'purple',
  blue: 'blue',
  green: 'green',
  amber: 'amber',
  red: 'red',
};

/** @type {Record<string, string>} */
const TITLE_COLOR_BY_VARIANT = {
  default: 'text-content',
  muted: 'text-content-muted',
  purple: 'text-purple-400',
  blue: 'text-blue-400',
  green: 'text-emerald-400',
  amber: 'text-amber-400',
  red: 'text-red-400',
};

/**
 * Highlight block for AI summaries, verified statements, context blocks, etc.
 * Built on `DrawerPanel` with optional icon + eyebrow + title.
 *
 * @param {string} [props.title]
 * @param {import('react').ReactNode} props.children  Body text or rich JSX.
 * @param {import('lucide-react').LucideIcon} [props.icon]  Pass a Lucide icon component, e.g. `import { Sparkles } from 'lucide-react'`.
 * @param {'default'|'muted'|'purple'|'blue'|'green'|'amber'|'red'} [props.variant='default']
 * @param {string} [props.eyebrow]  Small label above the title (uppercase-friendly).
 * @param {string} [props.className]  Applied to the outer `DrawerPanel`.
 */
const DrawerSummaryPanel = ({
  title,
  children,
  icon: Icon,
  variant = 'default',
  eyebrow,
  className = '',
}) => {
  const panelVariant = VARIANT_TO_PANEL[variant] ?? VARIANT_TO_PANEL.default;
  const titleColor = TITLE_COLOR_BY_VARIANT[variant] ?? TITLE_COLOR_BY_VARIANT.default;

  return (
    <DrawerPanel variant={panelVariant} className={className}>
      {(Icon || eyebrow || title) ? (
        <div className="mb-3 flex flex-wrap items-start gap-2">
          {Icon ? (
            <span className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-edge bg-surface-muted/50">
              <Icon className="size-4 text-content-secondary" aria-hidden />
            </span>
          ) : null}
          <div className="min-w-0 flex-1 space-y-1">
            {eyebrow ? (
              <p className="text-[10px] font-semibold uppercase tracking-wider text-content-muted">
                {eyebrow}
              </p>
            ) : null}
            {title ? (
              <p className={['text-sm font-semibold leading-snug', titleColor].filter(Boolean).join(' ')}>
                {title}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="text-sm leading-relaxed text-content-secondary">{children}</div>
    </DrawerPanel>
  );
};

DrawerSummaryPanel.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  icon: PropTypes.elementType,
  variant: PropTypes.oneOf(['default', 'muted', 'purple', 'blue', 'green', 'amber', 'red']),
  eyebrow: PropTypes.string,
  className: PropTypes.string,
};

export default memo(DrawerSummaryPanel);
